// GA4 event tracking via Measurement Protocol — Manifest V3 compliant.
// No remote scripts loaded. Just HTTP POST requests to GA4's endpoint.
//
// Set VITE_GA_MEASUREMENT_ID and VITE_GA_API_SECRET in .env (see .env.example).

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? "";
const GA_API_SECRET = import.meta.env.VITE_GA_API_SECRET ?? "";

const isConfigured =
  GA_MEASUREMENT_ID.length > 0 &&
  GA_API_SECRET.length > 0 &&
  !GA_MEASUREMENT_ID.includes("XXXXXXXX");

const GA_ENDPOINT = isConfigured
  ? `https://www.google-analytics.com/mp/collect?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${GA_API_SECRET}`
  : "";

// Debug endpoint — sends events but doesn't record them, returns validation messages
// Switch to this during development to verify events are formatted correctly:
// const GA_ENDPOINT = isConfigured
//   ? `https://www.google-analytics.com/debug/mp/collect?measurement_id=${GA_MEASUREMENT_ID}&api_secret=${GA_API_SECRET}`
//   : "";

async function getClientId(): Promise<string> {
  const chromeGlobal = (window as { chrome?: { storage?: { local: { get: Function; set: Function } } } }).chrome;
  if (chromeGlobal?.storage?.local) {
    return new Promise((resolve) => {
      chromeGlobal.storage!.local.get("ga_client_id", (result: { ga_client_id?: string }) => {
        if (result.ga_client_id) {
          resolve(result.ga_client_id);
        } else {
          const newId = generateClientId();
          chromeGlobal.storage!.local.set({ ga_client_id: newId });
          resolve(newId);
        }
      });
    });
  }

  let clientId = localStorage.getItem("ga_client_id");
  if (!clientId) {
    clientId = generateClientId();
    localStorage.setItem("ga_client_id", clientId);
  }
  return clientId;
}

function generateClientId(): string {
  const random = Math.floor(Math.random() * 1000000000);
  const timestamp = Math.floor(Date.now() / 1000);
  return `${random}.${timestamp}`;
}

function getSessionId(): string {
  const SESSION_KEY = "ga_session_id";
  const SESSION_TIMEOUT_KEY = "ga_session_timeout";
  const TIMEOUT_MS = 30 * 60 * 1000;

  const now = Date.now();
  const lastActivity = parseInt(localStorage.getItem(SESSION_TIMEOUT_KEY) || "0", 10);
  let sessionId = localStorage.getItem(SESSION_KEY);

  if (!sessionId || now - lastActivity > TIMEOUT_MS) {
    sessionId = Math.floor(Date.now() / 1000).toString();
    localStorage.setItem(SESSION_KEY, sessionId);
  }

  localStorage.setItem(SESSION_TIMEOUT_KEY, now.toString());
  return sessionId;
}

export async function trackEvent(
  eventName: string,
  params: Record<string, string | number | boolean> = {}
): Promise<void> {
  if (!GA_ENDPOINT) return;

  try {
    const clientId = await getClientId();
    const sessionId = getSessionId();

    const payload = {
      client_id: clientId,
      events: [
        {
          name: eventName,
          params: {
            session_id: sessionId,
            engagement_time_msec: 100,
            ...params,
          },
        },
      ],
    };

    fetch(GA_ENDPOINT, {
      method: "POST",
      body: JSON.stringify(payload),
    }).catch((err) => {
      console.debug("[Analytics] Failed to send event:", eventName, err);
    });
  } catch (err) {
    console.debug("[Analytics] Error:", err);
  }
}

export async function trackScreenView(screenName: string): Promise<void> {
  trackEvent("page_view", {
    page_title: screenName,
    page_location: screenName,
  });
}
